/**
 * Lead quick view — read a lead without leaving the queue.
 *
 * Deliberately NOT the full LeadDetailView in a dialog. The queue's question is
 * "do I understand this lead well enough to act?", so this shows the facts that
 * answer it and nothing else: who, where they came from, what state it is in,
 * what happens next, and what has already happened. Anything requiring the full
 * record — the tabs, the activity history, the link editors — is one click away
 * behind Open full lead.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  CalendarClock,
  ClipboardList,
  Mail,
  Phone,
  PhoneIncoming,
  Target,
  User,
} from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@command-center/ui-system';
import { formatSourceLabel } from '@command-center/oneup-portal-ui';
import { intakeRecordRoute } from '../lib/intake-routes';
import { leadloopApi, type IntakeItem, type LccLead } from '../lib/leadloop-api';
import { LeadActionCenter } from '../components/lead-action-center';
import { SendEmailDialog } from './SendEmailDialog';
import { Field, LeadStatusBadge, LoopReasonBadge, VerifiedTag, formatDateTime, loopActionLabel } from './parts';

export function LeadQuickView({
  lead,
  reason,
  dueAt,
  open,
  onOpenChange,
}: {
  lead: LccLead;
  reason?: string;
  dueAt?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  /*
    The email composer REPLACES this dialog rather than stacking on top of it:
    two overlaid modals leave the user unsure which Escape closes what, and the
    quick view has nothing more to offer once you have decided to write.
  */
  const [emailOpen, setEmailOpen] = useState(false);
  // Detail and activity load only while the dialog is open, so browsing the
  // queue does not fire a request per card.
  const detail = useQuery({
    queryKey: ['lead', lead.id],
    queryFn: () => leadloopApi.leadDetail(lead.id),
    enabled: open,
  });
  const activity = useQuery({
    queryKey: ['activity', lead.id],
    queryFn: () => leadloopApi.activity({ entityId: lead.id, limit: 5 }),
    enabled: open,
  });

  const full = detail.data?.lead ?? lead;
  const intake = detail.data?.intake ?? [];
  const latest = activity.data?.items?.[0];
  const summary = full.aiSummary ?? full.message;

  const handOffTo = (openNext: (value: boolean) => void) => {
    onOpenChange(false);
    openNext(true);
  };

  return (
    <>
      {/* Lives OUTSIDE the quick-view Dialog so closing it cannot bubble back
          into the card that opened the quick view in the first place. */}
      <SendEmailDialog
        leadId={lead.id}
        contextLabel={full.contactName ?? full.title}
        open={emailOpen}
        onOpenChange={setEmailOpen}
      />
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-6 text-left">
            <Target className="h-5 w-5 shrink-0 text-primary" aria-hidden />
            <span className="min-w-0 break-words">{full.title}</span>
            <LeadStatusBadge status={full.status} />
            <LoopReasonBadge reason={reason} />
            <VerifiedTag verified={full.verified} />
          </DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-x-3 gap-y-1 text-left">
            {full.contactName && (
              <span className="inline-flex items-center gap-1">
                <User className="h-3.5 w-3.5" aria-hidden />
                {full.contactName}
              </span>
            )}
            {full.companyName && (
              <span className="inline-flex items-center gap-1">
                <Building2 className="h-3.5 w-3.5" aria-hidden />
                {full.companyName}
              </span>
            )}
            {/* Verified is stated once, in the title row beside the lifecycle. */}
          </DialogDescription>
        </DialogHeader>

        {/*
          Contact details, as details. The phone number stays dialable but is no
          longer a bordered chip pretending to be an action button — a tel: link
          styled like a button reads as "do something", and on a desktop it does
          nothing. Email keeps its button because it opens the composer, which
          is a real action that records what was sent.
        */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {full.phone ? (
            <a
              href={`tel:${full.phone.replace(/[^+\d]/g, '')}`}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary hover:underline"
            >
              <Phone className="h-3.5 w-3.5" aria-hidden />
              {full.phone}
            </a>
          ) : null}
          {full.email ? (
            <button
              type="button"
              onClick={() => handOffTo(setEmailOpen)}
              className="inline-flex max-w-full items-center gap-1.5 truncate text-[13px] font-medium text-primary hover:underline"
            >
              <Mail className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">{full.email}</span>
            </button>
          ) : null}
          {!full.phone && !full.email && (
            <p className="text-[13px] text-muted-foreground">No phone or email on this lead yet.</p>
          )}
        </div>

        {summary && (
          <p className="whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 text-[13px] leading-relaxed text-foreground">
            {summary}
          </p>
        )}

        <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
          <Field label="Source" value={formatSourceLabel(full.source)} />
          <Field label="Owner" value={full.assignedTo} />
          <Field label="Created" value={formatDateTime(full.createdAt)} />
          <Field label="Next step" value={full.nextStep} />
          <Field
            label="Next follow-up"
            value={
              full.nextFollowUpAt || dueAt ? (
                <span className="inline-flex items-center gap-1.5">
                  <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                  {formatDateTime(full.nextFollowUpAt ?? dueAt)}
                </span>
              ) : undefined
            }
          />
          <Field
            label="Latest activity"
            value={
              latest ? (
                <span title={formatDateTime(latest.ts)}>
                  {latest.summary}
                  <span className="block text-xs text-muted-foreground">
                    {formatDateTime(latest.ts)}
                  </span>
                </span>
              ) : activity.isLoading ? (
                'Loading…'
              ) : undefined
            }
          />
        </dl>

        {intake.length > 0 && (
          <section>
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              Where it came from
            </h3>
            <ul className="mt-2 space-y-1.5">
              {intake.slice(0, 4).map((item) => (
                <li key={item.id}>
                  <IntakeRow
                    item={item}
                    onOpen={() => {
                      onOpenChange(false);
                      navigate(intakeRoute(item));
                    }}
                  />
                </li>
              ))}
            </ul>
            {intake.length > 4 && (
              <p className="mt-1.5 text-xs text-muted-foreground">
                +{intake.length - 4} more on the full lead
              </p>
            )}
          </section>
        )}

        {/*
          Acting on the lead happens here, not on the card behind this dialog:
          the queue's whole loop is read it, say what happened, set when it comes
          back, close. Writing a note used to mean opening a second modal on top
          of this one and answering a form about it first.
        */}
        <LeadActionCenter lead={full} recommendation={loopActionLabel(reason)} />

        <div className="flex flex-wrap items-center gap-2 pt-1">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1.5"
            disabled={!full.email}
            title={full.email ? undefined : 'This lead has no email address yet.'}
            onClick={() => handOffTo(setEmailOpen)}
          >
            <Mail className="h-3.5 w-3.5" aria-hidden />
            Send Email
          </Button>
          <Button type="button" size="sm" className="ml-auto gap-1.5" asChild>
            <Link to={`/leads/${lead.id}`}>
              Open full lead
              <ArrowRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
          </Button>
        </div>
      </DialogContent>
      </Dialog>
    </>
  );
}

/** Where a call or form entry opens. */
export function intakeRoute(item: IntakeItem): string {
  return intakeRecordRoute(item.sourceType, item.sourceId);
}

/** Compact one-line origin: which call or form entry produced this lead. */
function IntakeRow({ item, onOpen }: { item: IntakeItem; onOpen: () => void }) {
  const isCall = item.sourceType === 'call';
  const Icon = isCall ? PhoneIncoming : ClipboardList;
  return (
    <button
      type="button"
      onClick={onOpen}
      title={`Open this ${isCall ? 'call' : 'form entry'}`}
      className="flex w-full items-start gap-2 rounded-md border border-border px-2.5 py-2 text-left transition-colors hover:border-primary/50 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] text-foreground">
          {item.displayName ?? (isCall ? 'Unknown caller' : 'Form entry')}
        </p>
        {item.summary && (
          <p className="line-clamp-1 text-xs text-muted-foreground">{item.summary}</p>
        )}
      </div>
      <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
        {formatDateTime(item.occurredAt ?? item.createdAt)}
      </span>
    </button>
  );
}

/*
  snake_case source values render as their display label. The implementation
  moved to @command-center/oneup-portal-ui with the rest of the LeadLoop
  vocabulary; re-exported here so the two views that import it from this module
  keep working.
*/
export { formatSourceLabel } from '@command-center/oneup-portal-ui';
